import { apiService } from '@/feature/api/axios';
import { handleApiError } from '@/shared/utils/errorUtils';

import type {
  ICreateDeviceRequest,
  IDeviceDetails,
  IDeviceListResponse,
  IDeviceMutationResponse,
  IExtendDeviceRequest,
  IRentDeviceRequest,
} from '../types/device';

const BASE = '/api/ai-automation-admin/devices';

const emptyList = (): IDeviceListResponse => ({
  summary: { total: 0, available: 0, rented: 0, maintenance: 0, unfit: 0 },
  devices: [],
});

export const deviceService = {
  getDevices: async (params?: Record<string, unknown>): Promise<IDeviceListResponse> => {
    try {
      const api = apiService();
      const response = await api.get<IDeviceListResponse>(`${BASE}/`, params);
      const pagination = response.pagination as
        | (NonNullable<IDeviceListResponse['pagination']> & { total_count?: number })
        | undefined;
      const totalCount = pagination?.total_count ?? pagination?.total_count;
      return {
        summary: response.summary || emptyList().summary,
        devices: Array.isArray(response.devices) ? response.devices : [],
        pagination: pagination
          ? {
              ...pagination,
              total_count: totalCount,
            }
          : undefined,
      };
    } catch (error) {
      handleApiError(error, 'Failed to fetch devices. Please try again.');
      return emptyList();
    }
  },

  getDeviceById: async (id: string): Promise<IDeviceDetails | null> => {
    try {
      const api = apiService();
      return await api.get<IDeviceDetails>(`${BASE}/${id}/`);
    } catch (error) {
      handleApiError(error, 'Failed to get device details. Please try again.');
      return null;
    }
  },

  createDevice: async (data: ICreateDeviceRequest): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<ICreateDeviceRequest, IDeviceMutationResponse>(`${BASE}/`, data);
    } catch (error) {
      handleApiError(error, 'Failed to add device. Please try again.');
      return null;
    }
  },

  rentDevice: async (
    id: string,
    data: IRentDeviceRequest
  ): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<IRentDeviceRequest, IDeviceMutationResponse>(
        `${BASE}/${id}/rent/`,
        data
      );
    } catch (error) {
      handleApiError(error, 'Failed to assign device. Please try again.');
      return null;
    }
  },

  extendRental: async (
    id: string,
    data: IExtendDeviceRequest
  ): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<IExtendDeviceRequest, IDeviceMutationResponse>(
        `${BASE}/${id}/extend/`,
        data
      );
    } catch (error) {
      handleApiError(error, 'Failed to extend rental. Please try again.');
      return null;
    }
  },

  releaseDevice: async (id: string, notes?: string): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<{ notes?: string }, IDeviceMutationResponse>(
        `${BASE}/${id}/release/`,
        {
          notes,
        }
      );
    } catch (error) {
      handleApiError(error, 'Failed to release device. Please try again.');
      return null;
    }
  },

  putInMaintenance: async (id: string, notes?: string): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<{ notes?: string }, IDeviceMutationResponse>(
        `${BASE}/${id}/maintenance/`,
        { notes }
      );
    } catch (error) {
      handleApiError(error, 'Failed to move device to maintenance. Please try again.');
      return null;
    }
  },

  completeMaintenance: async (
    id: string,
    notes?: string
  ): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<{ notes?: string }, IDeviceMutationResponse>(
        `${BASE}/${id}/complete-maintenance/`,
        { notes }
      );
    } catch (error) {
      handleApiError(error, 'Failed to complete maintenance. Please try again.');
      return null;
    }
  },

  markUnfit: async (id: string, reason: string): Promise<IDeviceMutationResponse | null> => {
    try {
      const api = apiService();
      return await api.create<{ reason: string }, IDeviceMutationResponse>(`${BASE}/${id}/unfit/`, {
        reason,
      });
    } catch (error) {
      handleApiError(error, 'Failed to mark device as unfit. Please try again.');
      return null;
    }
  },
};
